import React, { useState, useEffect, useMemo } from 'react';
import {
  Users,
  Mail,
  Phone,
  Calendar,
  MapPin,
  Globe,
  AlertCircle,
  Check,
  X,
  BarChart3,
  Palette,
  Headphones,
  Clock,
  Sparkles,
  Receipt,
  CreditCard,
} from 'lucide-react';
import StatusControls from '../StatusControls';

const PLAN_FEATURE_ROWS = [
  { key: 'feature_analytics', label: 'Advanced analytics', Icon: BarChart3 },
  { key: 'feature_custom_branding', label: 'Custom branding', Icon: Palette },
  { key: 'feature_priority_support', label: 'Priority support', Icon: Headphones },
  { key: 'feature_advanced_booking', label: 'Advanced booking', Icon: Clock },
  { key: 'feature_multi_location', label: 'Multi-location', Icon: MapPin },
];

function calcTimeRemaining(endDate) {
  if (!endDate) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, expired: true };
  }
  const end = new Date(endDate).getTime();
  const now = Date.now();
  const timeLeft = end - now;
  if (timeLeft <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, expired: true };
  }
  return {
    days: Math.floor(timeLeft / (1000 * 60 * 60 * 24)),
    hours: Math.floor((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
    minutes: Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60)),
    seconds: Math.floor((timeLeft % (1000 * 60)) / 1000),
    expired: false,
  };
}

function formatMoney(amount, currency = 'USD') {
  const n = Number(amount);
  if (!Number.isFinite(n)) return '—';
  const c = (currency || 'USD').toUpperCase();
  if (c === 'USD') return `$${n.toFixed(2)}`;
  return `${c} ${n.toFixed(2)}`;
}

/** Prefer recorded term charge on vendor_subscriptions; never show stale catalog monthly price as “what they paid” for a yearly term. */
function resolveSubscriptionBilling(sub) {
  const plan = sub.subscription || {};
  const interval = sub.billing_interval || plan.billing_interval || 'monthly';
  const currency = (sub.currency || plan.currency || 'USD').toUpperCase();
  const termCharge = Number.parseFloat(sub.amount_paid);
  const hasTermCharge = Number.isFinite(termCharge) && termCharge > 0;

  const monthly = Number.parseFloat(plan.price_monthly);
  const yearly = Number.parseFloat(plan.price_yearly);
  const legacy = Number.parseFloat(plan.price);

  let listAtInterval = null;
  if (interval === 'yearly') {
    if (Number.isFinite(yearly) && yearly > 0) listAtInterval = yearly;
    else if (plan.billing_interval === 'yearly' && Number.isFinite(legacy) && legacy > 0) listAtInterval = legacy;
  } else {
    if (Number.isFinite(monthly) && monthly > 0) listAtInterval = monthly;
    else if (plan.billing_interval !== 'yearly' && Number.isFinite(legacy) && legacy > 0) listAtInterval = legacy;
  }

  const payments = Array.isArray(sub.payments) ? sub.payments : [];
  const lastCompleted =
    payments.find((p) => p.status === 'completed') ||
    payments.find((p) => p.paid_at) ||
    null;
  const lastRowAmount = lastCompleted != null ? Number.parseFloat(lastCompleted.amount) : null;
  const lastRowOk = Number.isFinite(lastRowAmount);

  const intervalLabel = interval === 'yearly' ? 'Yearly' : 'Monthly';

  return {
    currency,
    intervalLabel,
    displayAmount: hasTermCharge ? termCharge : listAtInterval,
    displayLabel: hasTermCharge
      ? 'Amount for this billing term (subscription record)'
      : 'Plan list price (catalog)',
    catalogListAtInterval: listAtInterval,
    lastPaymentAmount: lastRowOk ? lastRowAmount : null,
    lastPaymentStatus: lastCompleted?.status,
    termChargeRecorded: hasTermCharge,
    mismatchHint:
      hasTermCharge && lastRowOk && Math.abs(termCharge - lastRowAmount) > 0.009
        ? `Latest payment row shows ${formatMoney(lastRowAmount, currency)} — reconciled term total above is ${formatMoney(termCharge, currency)}.`
        : null,
  };
}

function buildPlanFeatures(plan) {
  if (!plan) return { bullets: [], rows: [] };

  const bullets = Array.isArray(plan.features_list)
    ? plan.features_list.filter(Boolean).map(String)
    : [];

  const rows = PLAN_FEATURE_ROWS.map(({ key, label, Icon }) => ({
    key,
    label,
    Icon,
    enabled: !!plan[key],
  }));

  return { bullets, rows };
}

const OverviewTab = ({
  vendor,
  vendorProfile,
  vendorSubscriptions = [],
  formatDate,
  onToggleFeatured,
  onToggleActive,
  isUpdatingFeatured,
  isUpdatingActive,
}) => {
  const [remainMap, setRemainMap] = useState({});

  const subsKey = useMemo(
    () =>
      (vendorSubscriptions || [])
        .map((s) => `${s.id}-${s.end_date}`)
        .join('|'),
    [vendorSubscriptions]
  );

  useEffect(() => {
    const tick = () => {
      const next = {};
      (vendorSubscriptions || []).forEach((sub, idx) => {
        const key = sub.id ?? idx;
        next[key] = calcTimeRemaining(sub.end_date);
      });
      setRemainMap(next);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [subsKey, vendorSubscriptions]);

  return (
    <div className="space-y-3 sm:space-y-6">
      {/* Basic Information */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">Basic Information</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                <Users className="w-4 h-4 text-gray-600" />
              </div>
              <div className="min-w-0 flex-1">
                <dt className="text-sm font-medium text-gray-500 mb-1">Full Name</dt>
                <dd className="text-sm font-semibold text-gray-900">{vendor?.name || 'N/A'}</dd>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                <Mail className="w-4 h-4 text-gray-600" />
              </div>
              <div className="min-w-0 flex-1">
                <dt className="text-sm font-medium text-gray-500 mb-1">Email Address</dt>
                <dd className="text-sm text-gray-900 break-all">{vendor?.email}</dd>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                <Phone className="w-4 h-4 text-gray-600" />
              </div>
              <div className="min-w-0 flex-1">
                <dt className="text-sm font-medium text-gray-500 mb-1">Phone Number</dt>
                <dd className="text-sm text-gray-900">{vendor?.phone || 'Not provided'}</dd>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                <Calendar className="w-4 h-4 text-gray-600" />
              </div>
              <div className="min-w-0 flex-1">
                <dt className="text-sm font-medium text-gray-500 mb-1">Joined Date</dt>
                <dd className="text-sm text-gray-900">{formatDate(vendor?.created_at)}</dd>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Business Information */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">Business Information</h3>
        {vendorProfile ? (
          <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Users className="w-4 h-4 text-blue-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <dt className="text-sm font-medium text-gray-500 mb-1">Business Name</dt>
                    <dd className="text-sm font-semibold text-gray-900">
                      {vendorProfile.business_name || 'Not provided'}
                    </dd>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                    <Phone className="w-4 h-4 text-gray-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <dt className="text-sm font-medium text-gray-500 mb-1">Business Phone</dt>
                    <dd className="text-sm text-gray-900">{vendorProfile.business_phone || 'Not provided'}</dd>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                    <Globe className="w-4 h-4 text-gray-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <dt className="text-sm font-medium text-gray-500 mb-1">Website</dt>
                    <dd className="text-sm text-gray-900">
                      {vendorProfile.website ? (
                        <a
                          href={vendorProfile.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-600 hover:text-primary-700 break-all"
                        >
                          {vendorProfile.website}
                        </a>
                      ) : (
                        'Not provided'
                      )}
                    </dd>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                    <MapPin className="w-4 h-4 text-gray-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <dt className="text-sm font-medium text-gray-500 mb-1">Address</dt>
                    <dd className="text-sm text-gray-900">{vendorProfile.address || 'Not provided'}</dd>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                  <MapPin className="w-4 h-4 text-gray-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <dt className="text-sm font-medium text-gray-500 mb-1">City, State</dt>
                  <dd className="text-sm text-gray-900">
                    {vendorProfile.city && vendorProfile.state
                      ? `${vendorProfile.city}, ${vendorProfile.state}`
                      : 'Not provided'}
                  </dd>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                  <MapPin className="w-4 h-4 text-gray-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <dt className="text-sm font-medium text-gray-500 mb-1">Zip Code</dt>
                  <dd className="text-sm text-gray-900">{vendorProfile.zip_code || 'Not provided'}</dd>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                  <MapPin className="w-4 h-4 text-gray-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <dt className="text-sm font-medium text-gray-500 mb-1">Country</dt>
                  <dd className="text-sm text-gray-900">{vendorProfile.country || 'Not provided'}</dd>
                </div>
              </div>
            </div>

            <div>
              <dt className="text-sm font-medium text-gray-500 mb-3">Service Categories</dt>
              <dd className="text-sm text-gray-900">
                {vendorProfile.vendor_categories?.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {vendorProfile.vendor_categories.map((category, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-blue-100 text-blue-800 border border-blue-200"
                      >
                        {category.service_category?.category_name || 'Unknown Category'}
                        {category.is_primary && (
                          <span className="ml-2 px-2 py-0.5 text-xs bg-blue-200 text-blue-700 rounded-full">Primary</span>
                        )}
                      </span>
                    ))}
                  </div>
                ) : (
                  <span className="text-gray-500">Not provided</span>
                )}
              </dd>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                  <Users className="w-4 h-4 text-gray-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <dt className="text-sm font-medium text-gray-500 mb-1">Team Size</dt>
                  <dd className="text-sm text-gray-900">{vendorProfile.team_size || 'Not provided'}</dd>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                  <Calendar className="w-4 h-4 text-gray-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <dt className="text-sm font-medium text-gray-500 mb-1">Years Experience</dt>
                  <dd className="text-sm text-gray-900">{vendorProfile.years_experience || 'Not provided'}</dd>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-12">
            <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-lg font-medium text-gray-500 mb-2">Business profile not uploaded</p>
            <p className="text-sm text-gray-400">This vendor has not completed their business profile setup yet.</p>
          </div>
        )}
      </div>

      {vendorProfile && (
        <div>
          <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-3 sm:mb-4">Business Description</h3>
          <p className="text-sm text-gray-700 bg-gray-50 p-4 rounded-lg">
            {vendorProfile.description || 'No description provided'}
          </p>
        </div>
      )}

      {/* Subscription Information */}
      {vendorSubscriptions.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
          <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-200">
            <Sparkles className="w-5 h-5 text-primary-600" />
            <h3 className="text-lg font-semibold text-gray-900">Subscription Information</h3>
          </div>
          <div className="space-y-8">
            {vendorSubscriptions.map((subscription, index) => {
              const plan = subscription.subscription;
              const { bullets, rows } = buildPlanFeatures(plan);
              const billing = resolveSubscriptionBilling(subscription);
              const key = subscription.id ?? index;
              const tr = remainMap[key] || calcTimeRemaining(subscription.end_date);

              return (
                <div key={key} className="border border-gray-100 rounded-xl overflow-hidden bg-gray-50/50">
                  <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 bg-white border-b border-gray-100">
                    <h4 className="text-base font-semibold text-gray-900">{plan?.name || 'Unknown Plan'}</h4>
                    <span
                      className={`px-2.5 py-1 text-xs font-medium rounded-full ${
                        subscription.status === 'active'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {subscription.status}
                    </span>
                  </div>

                  <div className="p-4 space-y-6">
                    {subscription.status === 'active' && subscription.end_date && !tr.expired && (
                      <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                        <div className="flex items-center justify-between mb-3">
                          <h5 className="text-sm font-semibold text-gray-900">Time remaining</h5>
                          <Calendar className="w-4 h-4 text-gray-400" />
                        </div>
                        <div className="grid grid-cols-4 gap-2 sm:gap-3">
                          {[
                            ['Days', tr.days],
                            ['Hours', tr.hours],
                            ['Min', tr.minutes],
                            ['Sec', tr.seconds],
                          ].map(([label, val]) => (
                            <div key={label} className="text-center">
                              <div className="text-xl sm:text-2xl font-bold text-gray-900">{val}</div>
                              <div className="text-[10px] sm:text-xs text-gray-500">{label}</div>
                            </div>
                          ))}
                        </div>
                        <p className="mt-3 text-center text-sm text-gray-600">Expires on {formatDate(subscription.end_date)}</p>
                      </div>
                    )}

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <div className="flex items-start gap-2 rounded-lg bg-white border border-gray-200 p-4">
                          <Receipt className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Billing</p>
                            <p className="text-lg font-bold text-gray-900">
                              {billing.displayAmount != null && Number.isFinite(billing.displayAmount)
                                ? formatMoney(billing.displayAmount, billing.currency)
                                : subscription.payment_method === 'free'
                                  ? 'Free'
                                  : '—'}
                              <span className="text-sm font-normal text-gray-600 ml-2">
                                / {billing.intervalLabel === 'Yearly' ? 'year' : 'month'}
                              </span>
                            </p>
                            <p className="text-xs text-gray-500 mt-1">{billing.displayLabel}</p>
                            {billing.catalogListAtInterval != null &&
                              billing.termChargeRecorded &&
                              Math.abs(billing.catalogListAtInterval - billing.displayAmount) > 0.02 && (
                                <p className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-md px-2 py-1.5 mt-2">
                                  Current catalog list for this interval:{' '}
                                  {formatMoney(billing.catalogListAtInterval, billing.currency)} (may differ from what
                                  was charged when they subscribed).
                                </p>
                              )}
                            {billing.mismatchHint && (
                              <p className="text-xs text-gray-600 bg-gray-100 rounded-md px-2 py-1.5 mt-2">{billing.mismatchHint}</p>
                            )}
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                          <div className="rounded-lg bg-white border border-gray-100 px-3 py-2">
                            <span className="text-gray-500 block text-xs mb-0.5">Billed as</span>
                            <span className="font-medium text-gray-900">{billing.intervalLabel}</span>
                          </div>
                          <div className="rounded-lg bg-white border border-gray-100 px-3 py-2">
                            <span className="text-gray-500 block text-xs mb-0.5">Auto-renew</span>
                            <span className="font-medium text-gray-900">{subscription.auto_renew ? 'Yes' : 'No'}</span>
                            <span className="block text-[11px] text-gray-400 mt-1">Vendor controls this from their dashboard.</span>
                          </div>
                          <div className="rounded-lg bg-white border border-gray-100 px-3 py-2">
                            <span className="text-gray-500 block text-xs mb-0.5">Start date</span>
                            <span className="font-medium text-gray-900">{formatDate(subscription.start_date)}</span>
                          </div>
                          <div className="rounded-lg bg-white border border-gray-100 px-3 py-2">
                            <span className="text-gray-500 block text-xs mb-0.5">End date</span>
                            <span className="font-medium text-gray-900">{formatDate(subscription.end_date)}</span>
                          </div>
                          <div className="rounded-lg bg-white border border-gray-100 px-3 py-2 sm:col-span-2 flex items-start gap-2">
                            <CreditCard className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                            <div>
                              <span className="text-gray-500 block text-xs mb-0.5">Payment method</span>
                              <span className="font-medium text-gray-900 capitalize">
                                {subscription.payment_method || 'N/A'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="rounded-lg bg-white border border-gray-200 p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <Sparkles className="w-4 h-4 text-primary-600" />
                          <h5 className="text-sm font-semibold text-gray-900">Plan features</h5>
                        </div>
                        {bullets.length > 0 && (
                          <ul className="space-y-2 mb-4 pb-4 border-b border-gray-100">
                            {bullets.map((line, i) => (
                              <li key={i} className="flex gap-2 text-sm text-gray-700">
                                <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                                <span>{line}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                        <ul className="space-y-2">
                          {rows.map(({ key, label, Icon, enabled }) => (
                            <li key={key} className="flex items-center justify-between gap-2 text-sm">
                              <span className="flex items-center gap-2 text-gray-700 min-w-0">
                                <Icon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                <span>{label}</span>
                              </span>
                              {enabled ? (
                                <Check className="w-4 h-4 text-green-500 flex-shrink-0" aria-label="Included" />
                              ) : (
                                <X className="w-4 h-4 text-gray-300 flex-shrink-0" aria-label="Not included" />
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <StatusControls
        vendorProfile={vendorProfile}
        vendor={vendor}
        onToggleFeatured={onToggleFeatured}
        onToggleActive={onToggleActive}
        isUpdatingFeatured={isUpdatingFeatured}
        isUpdatingActive={isUpdatingActive}
      />
    </div>
  );
};

export default OverviewTab;
